// src/components/vault/documents/id-validation/constants.ts

import type { ExtractedINEData, IDValidationState } from "./types";
import type { LucideIcon } from "lucide-react";
import { ScanSearch, FileText, UserCheck, ShieldCheck, CircleCheckBig } from "lucide-react";

/**
 * Dummy INE data that simulates OCR extraction results
 * This will be replaced with actual API response when backend is ready
 */
export const DUMMY_INE_DATA: ExtractedINEData = {
  nombre: "JUAN CARLOS",
  segundoNombre: "",
  apellidoPaterno: "GARCIA",
  apellidoMaterno: "LOPEZ",
  direccion: {
    calle: "AV. INSURGENTES SUR",
    numeroExterior: "1234",
    numeroInterior: "DEPTO 5",
    colonia: "DEL VALLE",
    codigoPostal: "03100",
    municipio: "BENITO JUAREZ",
    estado: "CIUDAD DE MEXICO",
  },
  curp: "GALJ850315HDFRPN01",
  rfc: "GALJ850315XXX",
  claveElector: "GRLPJN85031509H100",
  fechaNacimiento: "15/03/1985",
  seccionElectoral: "0109",
  vigencia: "2030",
  genero: "Masculino",
  email: "",
  phone_number: "",
  dial_code: "+52",
};

/**
 * Initial state for the ID validation flow
 */
export const INITIAL_VALIDATION_STATE: IDValidationState = {
  currentStep: "upload",
  documentType: null,
  extractedData: null,
  isAddressUpdated: null,
  hasProofOfAddress: true, // Default true to preserve existing behavior
  validationProgress: 0,
  error: null,
  invalidReason: null,
};

/**
 * Progress update interval in milliseconds
 */
export const PROGRESS_INTERVAL_MS = 100; // Update every 100ms

/**
 * Labels for document types
 */
export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  ine: "INE / IFE",
  passport: "Pasaporte",
  driver_license: "Licencia de Conducir",
  migratory_form: "Forma Migratoria",
};

/**
 * Labels for proof of address document types
 */
export const PROOF_OF_ADDRESS_TYPE_LABELS: Record<string, string> = {
  water_bill: "Recibo de Agua",
  electricity_bill: "Recibo de Luz",
  predial: "Predial",
  communications_bill: "Recibo de Comunicaciones",
  gas_bill: "Recibo de Gas",
  phone_bill: "Recibo Telefonico",
  bank_statement: "Estado de Cuenta Bancario",
};

// ============================================================================
// LOGARITHMIC PROGRESS CURVE
// ============================================================================

/**
 * Compute perceived progress using a logarithmic curve.
 *
 * Behavior:
 *  - Reaches ~50% at 6 seconds
 *  - Reaches ~80% at 15 seconds
 *  - Reaches ~90% at 30 seconds
 *  - Asymptotes at 95% (never exceeds — remaining 5% filled on API response)
 *
 * Formula: progress = 95 * (1 - e^(-k * t))
 *   where k = -ln(1 - 0.5/0.95) / 6 ≈ 0.1217
 */
const LOG_CURVE_K = 0.1217;
const LOG_CURVE_MAX = 95;

export function getLogarithmicProgress(elapsedMs: number): number {
  const elapsedSeconds = elapsedMs / 1000;
  const progress = LOG_CURVE_MAX * (1 - Math.exp(-LOG_CURVE_K * elapsedSeconds));
  return Math.min(progress, LOG_CURVE_MAX);
}

// ============================================================================
// VALIDATION STEP DEFINITIONS
// ============================================================================

export interface ValidationStepDefinition {
  key: string;
  icon: LucideIcon;
  label: string;
  /** Progress percentage where this step begins (inclusive) */
  startProgress: number;
  /** Progress percentage where this step ends (exclusive) */
  endProgress: number;
  /** Sub-messages that rotate while this step is active */
  subMessages: string[];
}

/**
 * 5 steps for ID document validation.
 * Mapped to logarithmic curve timing:
 *  Step 1 (0-20%):  ~0-2s
 *  Step 2 (20-45%): ~2-5s
 *  Step 3 (45-65%): ~5-10s
 *  Step 4 (65-85%): ~10-20s
 *  Step 5 (85-95%): ~20s+
 */
export const ID_VALIDATION_STEPS: ValidationStepDefinition[] = [
  {
    key: "receiving",
    icon: ScanSearch,
    label: "Recibiendo documento",
    startProgress: 0,
    endProgress: 20,
    subMessages: [
      "Preparando el analisis...",
      "Cargando imagen al servidor...",
    ],
  },
  {
    key: "analyzing",
    icon: FileText,
    label: "Analizando contenido",
    startProgress: 20,
    endProgress: 45,
    subMessages: [
      "Detectando tipo de documento...",
      "Identificando campos del documento...",
      "Leyendo informacion impresa...",
    ],
  },
  {
    key: "extracting",
    icon: UserCheck,
    label: "Extrayendo informacion",
    startProgress: 45,
    endProgress: 65,
    subMessages: [
      "Extrayendo datos personales...",
      "Leyendo direccion del documento...",
      "Procesando datos electorales...",
    ],
  },
  {
    key: "verifying",
    icon: ShieldCheck,
    label: "Verificando autenticidad",
    startProgress: 65,
    endProgress: 85,
    subMessages: [
      "Comprobando integridad del documento...",
      "Validando formato y estructura...",
      "Analizando elementos de seguridad...",
      "Verificando vigencia del documento...",
    ],
  },
  {
    key: "finalizing",
    icon: CircleCheckBig,
    label: "Finalizando validacion",
    startProgress: 85,
    endProgress: 95,
    subMessages: [
      "Preparando resultados...",
      "Consolidando informacion extraida...",
    ],
  },
];

/**
 * 5 steps for proof of address validation.
 */
export const PROOF_OF_ADDRESS_VALIDATION_STEPS: ValidationStepDefinition[] = [
  {
    key: "receiving",
    icon: ScanSearch,
    label: "Recibiendo comprobante",
    startProgress: 0,
    endProgress: 20,
    subMessages: [
      "Preparando el analisis...",
      "Cargando imagen al servidor...",
    ],
  },
  {
    key: "analyzing",
    icon: FileText,
    label: "Analizando comprobante",
    startProgress: 20,
    endProgress: 45,
    subMessages: [
      "Detectando tipo de comprobante...",
      "Identificando emisor del servicio...",
      "Leyendo informacion del recibo...",
    ],
  },
  {
    key: "extracting",
    icon: UserCheck,
    label: "Extrayendo domicilio",
    startProgress: 45,
    endProgress: 65,
    subMessages: [
      "Leyendo direccion del comprobante...",
      "Extrayendo datos del titular...",
      "Procesando periodo de facturacion...",
    ],
  },
  {
    key: "verifying",
    icon: ShieldCheck,
    label: "Verificando vigencia",
    startProgress: 65,
    endProgress: 85,
    subMessages: [
      "Comprobando fecha de emision...",
      "Validando que el comprobante este vigente...",
      "Verificando informacion del domicilio...",
    ],
  },
  {
    key: "finalizing",
    icon: CircleCheckBig,
    label: "Finalizando validacion",
    startProgress: 85,
    endProgress: 95,
    subMessages: [
      "Preparando resultados...",
      "Consolidando informacion extraida...",
    ],
  },
];

// ============================================================================
// STEP RESOLUTION HELPERS
// ============================================================================

/**
 * Given a progress value (0-100), return the index of the active step.
 */
export function getActiveStepIndex(
  progress: number,
  steps: ValidationStepDefinition[]
): number {
  if (progress >= 100) return steps.length - 1;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (progress >= steps[i].startProgress) return i;
  }
  return 0;
}

/** Rotation interval for sub-messages within a single step (ms). */
export const SUB_MESSAGE_ROTATION_MS = 5000;

/** Threshold in ms after which a fallback message is shown. */
export const LONG_WAIT_THRESHOLD_MS = 50000;

/** Fallback message shown when total elapsed time exceeds threshold. */
export const LONG_WAIT_MESSAGE =
  "Esto esta tomando un poco mas de lo esperado. Por favor espera...";

// ============================================================================
// LEGACY EXPORTS (backward compatibility)
// ============================================================================

export const VALIDATION_MESSAGES = {
  analyzing: "Analizando documento...",
  extracting: "Extrayendo informacion...",
  verifying: "Verificando datos...",
  completing: "Finalizando validacion...",
};

export function getValidationMessage(progress: number): string {
  if (progress < 25) return VALIDATION_MESSAGES.analyzing;
  if (progress < 50) return VALIDATION_MESSAGES.extracting;
  if (progress < 75) return VALIDATION_MESSAGES.verifying;
  return VALIDATION_MESSAGES.completing;
}

/**
 * Mexican states for address form
 */
export const MEXICAN_STATES = [
  "AGUASCALIENTES",
  "BAJA CALIFORNIA",
  "BAJA CALIFORNIA SUR",
  "CAMPECHE",
  "CHIAPAS",
  "CHIHUAHUA",
  "CIUDAD DE MEXICO",
  "COAHUILA",
  "COLIMA",
  "DURANGO",
  "ESTADO DE MEXICO",
  "GUANAJUATO",
  "GUERRERO",
  "HIDALGO",
  "JALISCO",
  "MICHOACAN",
  "MORELOS",
  "NAYARIT",
  "NUEVO LEON",
  "OAXACA",
  "PUEBLA",
  "QUERETARO",
  "QUINTANA ROO",
  "SAN LUIS POTOSI",
  "SINALOA",
  "SONORA",
  "TABASCO",
  "TAMAULIPAS",
  "TLAXCALA",
  "VERACRUZ",
  "YUCATAN",
  "ZACATECAS",
];
